import { useState, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { Link, useNavigate } from "react-router-dom"
import moment from "moment"
import { map, size } from "lodash"
import { Menu, MenuItem, Paper } from "@mui/material"
import { Refresh } from "@mui/icons-material"
import Tooltip from "components/material/tooltip"
import { PERMISSION_LEVEL } from "helpers/shapes"
import ResourceTooltipInfo from "components/resource-tooltip-info"
import ResourceIcon from "components/resource-icon"
import { upsellingTitle } from "helpers/op-pro"
import { getResourceTypeLabel } from "../../helpers"
import { FavoriteToggleButton, PermissionsButton, MenuButton } from "./buttons"

import "./style.scss"

export default function Resource({
  account,
  action,
  resource,
  resourceType = "portfolio",
  url,
  menuItems,
  onClick,
  toggleFavorite,
  trackResourceEvent,
  isOpPro,
  models,
  dispatch,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null)
  const navigate = useNavigate()

  const handleClick = () => {
    if (onClick) onClick(resource)
    trackResourceEvent({ trackType: "resources-go-to-resource", trackPayload: { resourceType } })
  }

  const toggleMenu = ev => {
    if (menuAnchor) {
      setMenuAnchor(null)
    } else {
      setMenuAnchor(ev.currentTarget)
    }
  }

  const openMenu = ev => {
    trackResourceEvent({ trackType: "resources-card-menu" })
    toggleMenu(ev)
  }

  const handleToggleFavorite = () => {
    const { id, isFavorite, ticker } = resource
    toggleFavorite(ticker || id, resourceType, !isFavorite)
  }

  const getMainAction = () => {
    if (!size(models)) {
      return <div className="resource-label-text"><Tooltip title={`Access to ${getResourceTypeLabel(resourceType)} is disabled since you do not have access to any risk model.`}><span>{resource.label}</span></Tooltip></div>
    }

    if (resourceType === "basket") {
      return <div className="resource-label-text" onClick={handleClick}>{resource.label}</div>
    }

    return <Link className="resource-label-text" onClick={handleClick} to={url}>{resource.label}</Link>
  }

  let permissionButtonProps = {
    title: "View permissions",
    disabled: false,
  }

  if (isOpPro && resourceType !== "portfolio") {
    permissionButtonProps = {
      title: upsellingTitle(isOpPro, resourceType),
      disabled: true,
    }
  }

  const makeOnClick = useCallback((action) => () => {
    setMenuAnchor(null)
    if (!action) { return }
    action(dispatch, navigate)
  }, [dispatch, navigate])

  const renderedMenuItems = useMemo(() => map(menuItems, ({ id, label, action, to, className }) => {
    if (to) {
      return <MenuItem key={id} className={classNames(className)} component={Link} to={to}>{label}</MenuItem>
    }
    return <MenuItem key={id} className={classNames(className)} onClick={makeOnClick(action)}>{label}</MenuItem>
  }), [menuItems, makeOnClick])

  const defaultTab = isOpPro ? "applications" : "members"
  const title = !size(models) ? {} : { title: resource.label }

  return <>
    <Paper elevation={1} className={classNames("resource", `${resourceType}-type-resource`, { disabled: !size(models) })} id={`resource-${resourceType}-${resource.alias}`} square>
      <div className="resource-header-wrapper">
        <ResourceIcon type={resourceType} />
        <div className="resource-summary-wrapper">
          <div className="resource-label" {...title}>
            {getMainAction()}
            {resource.info && resource.info.length > 0
              && <ResourceTooltipInfo info={resource.info} alias={resource.alias} label={resource.label} />}
          </div>
          <div className="resource-type">
            {resource.refresh && <div className="uploading">Uploading positions <Refresh className="refresh-button" onClick={() => window.location.reload()} /></div>}
            {!resource.refresh && resource.noData && <span className="no-data">No data</span>}
            {!resource.refresh && resource.lastUpdated && !resource.noData && `Updated ${moment(resource.lastUpdated).fromNow()}`}
          </div>
        </div>
      </div>
      <div className="content-wrapper">
        {resource.description && <div className="resource-description" title={resource.description}>
          {resource.description}
        </div>}
      </div>
      <div className={classNames("bottom-wrapper", { action: !!action })}>
        <div className="resource-metadata">
          {action && <div className="resource-metadata-wrapper">
            {action}
          </div>}
        </div>
        <div className="actions-wrapper">
          <div className="actions-menu">
            <FavoriteToggleButton isFavorite={resource.isFavorite} handleToggleFavorite={handleToggleFavorite} />
            <PermissionsButton account={account} {...permissionButtonProps} alias={resource.alias} resourceType={resource.resourceType} defaultTab={defaultTab} />
            <MenuButton openMenu={openMenu} />
          </div>
        </div>
      </div>
    </Paper>
    <Menu
      anchorEl={menuAnchor}
      open={!!menuAnchor}
      onClose={toggleMenu}
      classes={{ paper: "resource-menu" }}
    >
      {renderedMenuItems}
    </Menu>
  </>
}

Resource.propTypes = {
  account: PropTypes.string.isRequired,
  action: PropTypes.object,
  url: PropTypes.string,
  resource: PropTypes.object.isRequired,
  resourceType: PropTypes.string.isRequired,
  permissionLevel: PERMISSION_LEVEL.isRequired,
  menuItems: PropTypes.array.isRequired,
  onClick: PropTypes.func,
  toggleFavorite: PropTypes.func.isRequired,
  trackResourceEvent: PropTypes.func.isRequired,
  isOpPro: PropTypes.bool.isRequired,
  models: PropTypes.object,
  model: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
}
